import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ShoppingCart, Check } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import { useSanityProduct } from '../hooks/useSanityProducts';
import { products as staticProducts } from '../data/products';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../data/seoMeta';

// Helper to convert YouTube URL to embed URL
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  var shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) return 'https://www.youtube.com/embed/' + shortsMatch[1] + '?autoplay=1&mute=1';
  var shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (shortMatch) return 'https://www.youtube.com/embed/' + shortMatch[1] + '?autoplay=1&mute=1';
  var watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
  if (watchMatch) return 'https://www.youtube.com/embed/' + watchMatch[1] + '?autoplay=1&mute=1';
  return url;
}

function BrassPlankPage() {
  var { t, language, formatPrice } = useTranslation();
  var { product: sanityProduct } = useSanityProduct('brass-plank');
  var navigate = useNavigate();
  // Static fallback (Sanity may be unavailable / 402'd) — pulls latest brass-plank data
  var staticBrassPlank = staticProducts.find(function(p) { return p.slug === 'brass-plank'; }) || {};
  
  var product = {
    name: 'The Brass Plank',
    price: 34.99,
    originalPrice: 39.99,
    image: 'https://static.wixstatic.com/media/4a2ebb_99eb55f9a9aa4a8e822cbb0e3eedcf04~mv2.jpg',
  };

  function handleBuyNow() { navigate('/product/brass-plank'); }

  // Get language-specific video (Sanity first, static fallback second)
  var videoUrl = null;
  var videosByLang = (sanityProduct && sanityProduct.videosByLanguage) || staticBrassPlank.videosByLanguage || null;
  if (videosByLang) {
    videoUrl = videosByLang[language] || videosByLang['en'] || null;
  }
  if (!videoUrl) {
    videoUrl = (sanityProduct && sanityProduct.youtubeUrl) || staticBrassPlank.youtubeUrl || null;
  }
  var embedUrl = getYouTubeEmbedUrl(videoUrl);

  var features = [
    t('brassPlankFeature1'),
    t('brassPlankFeature2'),
    t('brassPlankFeature3'),
    t('brassPlankFeature4'),
    t('brassPlankFeature5'),
    t('brassPlankFeature6'),
    t('brassPlankFeature7')
  ];

  return (
    <Layout>
      <SEO title={PAGE_SEO['brass-plank'].title} description={PAGE_SEO['brass-plank'].description} url="/brass-plank" />
      <div className="pt-32 pb-20 bg-black min-h-screen">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">The Brass Plank</h1>
            <p className="text-2xl text-[#d4af37] font-semibold">
              {t('brassPlankTagline')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-start mb-12">
            <div className="relative space-y-6">
              {embedUrl ? (
                <>
                  <iframe
                    src={embedUrl}
                    title="Brass Plank Video"
                    className="w-full aspect-video rounded-2xl bg-neutral-900"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  <img
                    src={product.image}
                    alt="The Brass Plank"
                    className="w-full aspect-square object-cover rounded-2xl"
                    referrerPolicy="no-referrer"
                  />
                </>
              ) : (
                <img
                  src={product.image}
                  alt="The Brass Plank"
                  className="w-full aspect-square object-cover rounded-2xl"
                  referrerPolicy="no-referrer"
                />
              )}
            </div>

            <div>
              <p className="text-neutral-300 text-lg leading-relaxed mb-8">
                {t('brassPlankDescription')}
              </p>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-white mb-4">{t('whyYouLoveIt')}</h3>
                <ul className="grid grid-cols-1 gap-3">
                  {features.map(function(feature, index) {
                    return (
                      <li key={index} className="flex items-center gap-3 text-neutral-300">
                        <span className="w-6 h-6 rounded-full bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-[#d4af37]" />
                        </span>
                        {feature}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <p className="text-[#d4af37] italic text-lg mb-8">
                {t('brassPlankCta')}
              </p>

              <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 mb-8">
                <div className="flex items-baseline gap-4">
                  <span className="text-3xl font-bold text-[#d4af37]">{formatPrice(product.price)}</span>
                  <span className="text-lg text-neutral-600 line-through">{formatPrice(product.originalPrice)}</span>
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                    {t('save')} {formatPrice(product.originalPrice - product.price)}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleBuyNow}
                data-testid="brassplank-buy-btn"
                className="w-full flex items-center justify-center gap-3 bg-[#d4af37] text-black hover:bg-white rounded-full px-8 py-5 font-semibold text-lg transition-all duration-300"
              >
                <ShoppingCart className="w-6 h-6" />
                {t('buyNow')}
              </button>

              <p className="text-center text-sm text-neutral-500 mt-4">
                {t('secureCheckout')}
              </p>
            </div>
          </div>

          <div className="text-center">
            <Link to="/shop" className="text-[#d4af37] hover:underline text-lg">
              ← {t('backToShop')}
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default BrassPlankPage;
